<script lang="ts">
  import type { Component } from "svelte";
  import { formatGbp } from "../lib/formatting";
  import { eachCons } from "../lib/iterable";
  import Check from "~icons/ph/check-bold";

  type Step = {
    name: string;
    icon: Component;
    label: string;
    subLabel: string;
    button: string;
    additionalText?: string | null;
    status?: "completed" | "current" | "todo";
  };

  let {
    title,
    amount,
    steps: providedSteps,
    current,
    showButtonRow = false
  } : {
    title: string;
    amount: number;
    steps: Step[];
    current: string;
    showButtonRow?: boolean;
  } = $props();

  let steps = $derived.by(()=>{
    let found = false
    return providedSteps.map((step)=>{
      let status = "todo";
      if(step.name === current) { status = "current"; found = true }
      else if(!found) status = "completed";
      return {...step, status};
    })
  }) as Step[];
  let currentStep = $derived(steps.find((s)=>s.status == "current"))
</script>

<div class="bg-gradient-to-bl from-emerald-100 to-[#F8FCFF] to-[90%] flex flex-col p-4 gap-4">
  <div class="flex justify-between text-emerald-700">
    <p class="bg-emerald-100 inline-flex items-center px-2 rounded-[32px]">{title}</p>
    <p class="text-2xl font-bold">
      {formatGbp(Math.floor(amount), 0)}
    </p>
  </div>

  <div class="-mx-4">
    <div class="w-full grid grid-flow-col auto-cols-fr">
      {#each [...eachCons([null, ...steps])] as [prev, curr], index}
        {@render step(prev, curr!, index === (steps.length - 1))}
      {/each}
    </div>
  </div>

  {#if showButtonRow}
    <div class="flex justify-between items-center gap-4">
      <p class="text-sm font-medium text-balance">
        {currentStep?.additionalText}
      </p>

      <button type="submit" class="btn btn-primary btn-sm rounded-[32px]">
        {currentStep?.button ?? "View details"}
      </button>
    </div>
  {/if}
</div>

{#snippet stepText(label: string, subLabel: string, status: string, showSubLabel: boolean)}
  <div class="flex flex-col items-center">
    <div class={status == "current" ? "text-black font-bold" : "text-gray-500"}>{label}</div>
    <div
      class="text-gray-500 text-xs font-medium leading-3 tracking-tight whitespace-nowrap hidden"
      class:!block={showSubLabel}>
      {subLabel}
    </div>
  </div>
{/snippet}

{#snippet stepIcon(step: Step)}
  {@const Icon = step.status == "completed" ? Check : step.icon}
  {@const color = {
    todo: "bg-gray-200 text-gray-500",
    current: "bg-sky-200 text-sky-700",
    completed: "bg-emerald-500 text-white",
  }[step.status!]}

  <div class="{color} w-8 h-8 m-1 rounded-full flex items-center justify-center">
    <Icon />
  </div>
{/snippet}

{#snippet step(prev : Step | null, step : Step, last : boolean)}
  {@const first = prev === null}
  {@const leftColor = prev?.status === "completed" ? "bg-emerald-500" : "bg-gray-200"}
  {@const rightColor = step.status === "completed" ? "bg-emerald-500" : "bg-gray-200"}

  <div class="flex flex-col items-stretch">
    <div class="flex flex-row items-center">
      <hr class={["grow h-0.5", leftColor, first && "invisible"]} />
      {@render stepIcon(step)}
      <hr class={["grow h-0.5", rightColor, last && "invisible"]} />
    </div>

    {@render stepText(
      step.label,
      step.subLabel,
      step.status!,
      step.status === "current" || (last && step.status === "completed"),
    )}
  </div>
{/snippet}
